import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type ReactElement, type ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Channel, ChannelSettingSummary } from '../../api';
import { SectionHeader } from '../../components/SectionHeader';
import {
  type ChannelsView,
  EmptyChannels,
  filterChannels,
  ReasonText,
  type ScreenFailure,
} from '../onboarding';

import { CopiedChannelRow } from './CopiedChannelRow';
import { OtherChannelRow } from './OtherChannelRow';
import { copiedRowView, tabSections } from './channels-tab-model';

// MUI's Alert is role="alert" by default; these messages are statuses (S9 R19). A constant, not a text.
const STATUS_ROLE = 'status';

/** A status message of E12: a copy just made (R6), or a removal E7 reported (R16). */
export type ChannelsNotice =
  | { readonly kind: 'added'; readonly name: string }
  | { readonly kind: 'removed'; readonly name: string | null };

/** Props of {@link ChannelsTabView}. */
export interface ChannelsTabViewProps {
  readonly view: ChannelsView;
  readonly settings: ReadonlyMap<string, ChannelSettingSummary>;
  readonly busy: ReadonlySet<string>;
  readonly writeFailure: ScreenFailure | null;
  readonly notice: ChannelsNotice | null;
  readonly onCopy: (channel: Channel) => void;
  readonly onReload: () => void;
  readonly onJoin: (url: string) => void;
  readonly onSignInAgain: () => void;
}

function Section({
  id,
  title,
  children,
}: {
  readonly id: string;
  readonly title: string;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <Stack component="section" aria-labelledby={id} spacing={1}>
      <SectionHeader id={id} title={title} />
      {children}
    </Stack>
  );
}

/**
 * Screen E12 · Channels tab (specs/S9-channels-tab/spec.md; US-07; `GestionCanaux.html`).
 * Presentational: no request, no navigation. Inside `AppLayout`, which owns the `main` landmark.
 * E3's loading, empty, failure and search behavior are reused (S9 R13); no step, checkbox, count
 * or Continue (R13, AC20).
 */
export function ChannelsTabView(props: ChannelsTabViewProps): ReactElement {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const copiedId = useId();
  const othersId = useId();
  const { view } = props;
  const channels = view.kind === 'list' ? view.channels : [];
  // R2: N counts every copied channel, whatever the search.
  const total = tabSections(channels).copied.length;
  const shown = tabSections(filterChannels(channels, query));
  const noResult = shown.copied.length === 0 && shown.others.length === 0;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 2 }}>
      <Typography variant="h5" component="h1">
        {t('channels.title')}
      </Typography>

      {props.notice === null ? null : (
        <Alert severity="info" role={STATUS_ROLE}>
          {props.notice.kind === 'added'
            ? t('channels.added', { name: props.notice.name })
            : props.notice.name === null
              ? t('channels.removedUnnamed')
              : t('onboarding.channels.removed', { name: props.notice.name })}
        </Alert>
      )}

      {view.kind === 'loading' ? (
        <Stack spacing={1} aria-busy="true" aria-label={t('onboarding.channels.loading')}>
          {[0, 1, 2, 3].map((n) => (
            <Skeleton key={n} variant="rounded" height={56} />
          ))}
        </Stack>
      ) : null}

      {view.kind === 'failed' ? (
        <Stack spacing={1.5}>
          <ReasonText failure={view.failure} />
          {view.failure.reasonCode === 'TELEGRAM_SESSION_LOST' ? (
            <Button variant="contained" onClick={props.onSignInAgain}>
              {t('onboarding.channels.signInAgain')}
            </Button>
          ) : (
            <Button variant="outlined" onClick={props.onReload}>
              {t('onboarding.channels.retry')}
            </Button>
          )}
        </Stack>
      ) : null}

      {view.kind === 'list' && channels.length === 0 ? (
        <EmptyChannels
          affiliateChannelUrl={view.affiliateChannelUrl}
          onJoin={props.onJoin}
          onReload={props.onReload}
        />
      ) : null}

      {view.kind === 'list' && channels.length > 0 ? (
        <>
          <TextField
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            placeholder={t('onboarding.channels.searchPlaceholder', { count: channels.length })}
            slotProps={{ htmlInput: { 'aria-label': t('onboarding.channels.searchLabel') } }}
          />
          {/* P8: under the search, so a failure is seen without scrolling 300 rows. */}
          {props.writeFailure === null ? null : <ReasonText failure={props.writeFailure} />}
          {noResult ? (
            <Typography>{t('onboarding.channels.noResult', { query: query.trim() })}</Typography>
          ) : (
            <>
              <Section id={copiedId} title={t('channels.copiedTitle', { total })}>
                {total === 0 ? (
                  <Typography sx={{ color: 'text.secondary' }}>
                    {t('channels.noneCopied')}
                  </Typography>
                ) : shown.copied.length === 0 ? null : (
                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    <List disablePadding>
                      {shown.copied.map((channel) => (
                        <CopiedChannelRow
                          key={channel.id}
                          channel={channel}
                          view={copiedRowView(channel, props.settings.get(channel.subscription.id))}
                        />
                      ))}
                    </List>
                  </Paper>
                )}
              </Section>
              {shown.others.length === 0 ? null : (
                <Section id={othersId} title={t('channels.othersTitle')}>
                  <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                    <List disablePadding>
                      {shown.others.map((channel) => (
                        <OtherChannelRow
                          key={channel.id}
                          channel={channel}
                          busy={props.busy.has(channel.id)}
                          onCopy={props.onCopy}
                        />
                      ))}
                    </List>
                  </Paper>
                </Section>
              )}
            </>
          )}
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {t('channels.footer')}
          </Typography>
        </>
      ) : null}
    </Box>
  );
}
